/**
 * AdminPeopleDirectory — one way to find a person across the ecosystem.
 *
 * Tabs:
 *   • Directory  — search, then a profile: who they are, which modules hold a
 *                  record, their pledges, their giving statement, and a pledge
 *                  straight to a campaign or an assembly fund.
 *   • Duplicates — suggested duplicate pairs, one at a time, confirmed or
 *                  rejected by a human. Confirming links; it never merges.
 *
 * Route: /admin/people
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PersonPicker } from '@/components/people/PersonPicker';
import { getToken } from '@/lib/authToken';
import { Users, HeartHandshake, Loader2, GitMerge, UserX, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, options = {}) => {
  const res = await axios({
    url: `${API_URL}/api${path}`,
    method: options.method || 'GET',
    data: options.body ? JSON.parse(options.body) : undefined,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  return res.data;
};

const money = (value, currency = 'USD') => `${currency} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// ==================== PROFILE ====================

const PersonProfile = ({ person, onPledged }) => {
  const year = new Date().getFullYear();
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', currency: 'USD', target: '', purpose: '', due_date: '' });

  const { data: statement, isLoading, isError } = useQuery({
    queryKey: ['giving-statement', person.identity_key, year],
    queryFn: () => api(`/people/${encodeURIComponent(person.identity_key)}/giving-statement?year=${year}`),
    enabled: !!person,
  });
  const { data: campaigns } = useQuery({
    queryKey: ['campaign-picker'],
    queryFn: () => api('/campaigns/admin/list'),
  });
  const { data: churches } = useQuery({
    queryKey: ['church-picker'],
    queryFn: () => api('/churches?limit=100'),
  });

  const pledge = useMutation({
    mutationFn: (payload) => api(`/people/${encodeURIComponent(person.identity_key)}/pledges`, {
      method: 'POST', body: JSON.stringify(payload),
    }),
    onSuccess: (d) => {
      toast.success(`Pledge recorded for ${person.name}`, {
        description: d.kind === 'campaign' ? 'Against the campaign you chose' : 'Against the assembly fund you chose',
      });
      setPledgeOpen(false);
      setForm({ amount: '', currency: 'USD', target: '', purpose: '', due_date: '' });
      onPledged?.();
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message),
  });

  const allCampaigns = campaigns?.campaigns || campaigns?.items || (Array.isArray(campaigns) ? campaigns : []);
  // A pledge belongs to an appeal that is actually running.
  const campaignList = allCampaigns.filter((c) => c.is_published !== false && c.status !== 'archived');
  const churchList = churches?.churches || churches?.items || (Array.isArray(churches) ? churches : []);

  return (
    <div className="space-y-4" data-testid="person-profile">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg">{person.name}</CardTitle>
              <CardDescription>
                {(person.modules || []).join(' · ')}
                {person.references?.length ? ` · ${person.references.join(', ')}` : ''}
              </CardDescription>
              <p className="mt-1 text-xs text-slate-500">
                {person.email_masked || person.phone_masked || 'no contact on record'}
              </p>
            </div>
            <Button onClick={() => setPledgeOpen(true)} data-testid="profile-new-pledge-btn">
              <HeartHandshake className="mr-1 h-4 w-4" /> Make a pledge
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500">
            One person, several relationships. Recording a pledge here links it to this person and changes
            nothing about their membership, registration or enrolment.
          </p>
        </CardContent>
      </Card>

      <Card data-testid="giving-statement-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-700" /> Giving statement · {year}
          </CardTitle>
          <CardDescription>Assembly by assembly. Figures are never pooled between assemblies or campaigns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isError ? (
            <p className="text-sm text-rose-700" data-testid="statement-error">
              The statement could not be loaded. Try again shortly.
            </p>
          ) : isLoading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Given this year</p>
                  {Object.keys(statement?.given_by_currency || {}).length === 0 ? (
                    <p className="text-lg font-semibold text-slate-900">—</p>
                  ) : (
                    Object.entries(statement.given_by_currency).map(([code, value]) => (
                      <p key={code} className="text-lg font-semibold text-slate-900">{money(value, code)}</p>
                    ))
                  )}
                </div>
                {[['Pledged', statement?.pledged], ['Paid on pledges', statement?.pledges_paid],
                  ['Still promised', statement?.pledges_outstanding]].map(([label, value]) => (
                    <div key={label} className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs text-slate-500">{label}</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-[10px] text-slate-400">across every currency pledged</p>
                    </div>
                  ))}
              </div>

              {(statement?.assemblies || []).length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Assembly</TableHead>
                      <TableHead>Gifts</TableHead>
                      <TableHead>What for</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statement.assemblies.map((a) => (
                      <TableRow key={a.church_id || 'unassigned'}>
                        <TableCell className="text-sm">{a.church_name || 'Unassigned'}</TableCell>
                        <TableCell className="text-sm">{a.gifts}</TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {Object.entries(a.by_type || {}).map(([k, v]) => `${k} ${money(v, a.currency)}`).join(' · ')}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">{money(a.total, a.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {statement?.hidden_by_permission > 0 && (
                <p className="text-xs text-amber-700" data-testid="statement-hidden-note">
                  {statement.hidden_by_permission} record(s) in assemblies you do not oversee are not shown.
                </p>
              )}

              {[['Campaign pledges', statement?.campaign_pledges], ['Assembly pledges', statement?.church_pledges]]
                .filter(([, rows]) => (rows || []).length > 0)
                .map(([label, rows]) => (
                  <div key={label}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                    <ul className="space-y-1">
                      {rows.map((p) => (
                        <li key={p.id} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm">
                          <span>{p.purpose || p.category || 'Pledge'}</span>
                          <span className="text-slate-600">
                            {money(p.paid, p.currency)} of {money(p.amount, p.currency)}
                            {p.outstanding > 0 && <Badge variant="outline" className="ml-2 text-[10px]">{money(p.outstanding, p.currency)} to go</Badge>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={pledgeOpen} onOpenChange={setPledgeOpen}>
        <DialogContent data-testid="profile-pledge-dialog">
          <DialogHeader><DialogTitle>Pledge for {person.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Where does it belong?</Label>
              <Select
                value={form.target}
                onValueChange={(v) => {
                  // Take the appeal's own currency, so a pledge is never
                  // recorded in one the campaign does not accept.
                  const [kind, id] = v.split(':');
                  const chosen = kind === 'campaign'
                    ? campaignList.find((c) => String(c.id) === id)
                    : churchList.find((c) => String(c.id) === id);
                  setForm((f) => ({ ...f, target: v, currency: chosen?.currency || f.currency }));
                }}
              >
                <SelectTrigger className="mt-1" data-testid="pledge-target-select">
                  <SelectValue placeholder="Choose a campaign or an assembly" />
                </SelectTrigger>
                <SelectContent>
                  {campaignList.map((c) => (
                    <SelectItem key={`c:${c.id}`} value={`campaign:${c.id}`}>Campaign · {c.name}</SelectItem>
                  ))}
                  {churchList.map((c) => (
                    <SelectItem key={`a:${c.id}`} value={`church:${c.id}`}>Assembly · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" min="0" step="0.01" value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       className="mt-1" data-testid="pledge-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                       className="mt-1" data-testid="pledge-currency-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">What for</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                     placeholder="e.g. roof fund" className="mt-1" data-testid="pledge-purpose-input" />
            </div>
            <div>
              <Label className="text-xs">By when (optional)</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     className="mt-1" data-testid="pledge-due-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPledgeOpen(false)} data-testid="pledge-cancel">Cancel</Button>
            <Button
              disabled={pledge.isPending}
              onClick={() => {
                const amount = parseFloat(form.amount);
                if (!form.target) { toast.error('Choose a campaign or an assembly'); return; }
                if (!amount || amount <= 0) { toast.error('A pledge has to be for something'); return; }
                const [kind, id] = form.target.split(':');
                pledge.mutate({
                  amount,
                  currency: form.currency || 'USD',
                  campaign_id: kind === 'campaign' ? id : '',
                  church_id: kind === 'church' ? id : '',
                  purpose: form.purpose,
                  due_date: form.due_date,
                });
              }}
              data-testid="pledge-confirm"
            >
              {pledge.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Record pledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== DUPLICATE REVIEW ====================

const DuplicateReview = () => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  // Pairs skipped this sitting. Skipping is not a decision, so it is not
  // written down — but the queue must not hand the same pair back for ever.
  const [skipped, setSkipped] = useState([]);
  const { data, isFetching, refetch, isError } = useQuery({
    queryKey: ['duplicate-next', skipped],
    queryFn: () => api(`/people/duplicates/next${skipped.length ? `?exclude=${encodeURIComponent(skipped.join(','))}` : ''}`),
  });

  const decide = useMutation({
    mutationFn: (payload) => api('/people/duplicates/decide', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (d) => {
      toast.success(d.action === 'same_person'
        ? `Linked — ${d.linked_records?.length || 0} record(s) now point at one person`
        : 'Noted as two different people; this pair will not come back');
      setReason('');
      setSkipped([]);
      refetch();
      queryClient.invalidateQueries({ queryKey: ['duplicate-history'] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message),
  });

  const { data: history } = useQuery({
    queryKey: ['duplicate-history'],
    queryFn: () => api('/people/duplicates/history'),
  });

  const candidate = data?.candidate;

  const side = (entry, label) => (
    <div className="flex-1 rounded-lg border border-slate-200 p-3" data-testid={`duplicate-${label}`}>
      <p className="text-sm font-semibold text-slate-900">{entry.name}</p>
      <p className="text-xs text-slate-500">{entry.email_masked || entry.phone_masked || 'no contact'}</p>
      <div className="mt-2 flex flex-wrap gap-1">
        {(entry.modules || []).map((m) => <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>)}
      </div>
      {entry.references?.length > 0 && (
        <p className="mt-2 text-[11px] text-slate-500">{entry.references.join(', ')}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <Card data-testid="duplicate-review-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-indigo-600" /> Suggested duplicates
            {data?.pending > 0 && <Badge variant="outline">{data.pending} waiting</Badge>}
          </CardTitle>
          <CardDescription>
            Pairs that share an email or phone number. A shared name is never offered — two people do share a name.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isError ? (
            <p className="text-sm text-rose-700" data-testid="duplicate-error">
              The queue could not be loaded. Try again shortly.
            </p>
          ) : isFetching ? (
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          ) : !candidate ? (
            <p className="text-sm text-emerald-700" data-testid="duplicate-queue-empty">
              {data?.message || 'Nothing waiting.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-slate-500">
                Matched on {candidate.matched_on}: <span className="font-mono">{candidate.matched_value_masked}</span>
                {candidate.same_name ? ' · the names match too' : ' · the names differ'}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {side(candidate.left, 'left')}
                {side(candidate.right, 'right')}
              </div>
              <div>
                <Label className="text-xs">How do you know?</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                       placeholder="e.g. same person, confirmed with her at the office"
                       className="mt-1" data-testid="duplicate-reason-input" />
              </div>
              <p className="text-xs text-slate-500">{data?.note}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    if (reason.trim().length < 4) { toast.error('Say briefly how you know'); return; }
                    decide.mutate({ pair_key: candidate.pair_key, action: 'same_person', reason });
                  }}
                  disabled={decide.isPending}
                  data-testid="duplicate-confirm-btn"
                >
                  {decide.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Same person — link them
                </Button>
                <Button
                  variant="outline"
                  onClick={() => decide.mutate({ pair_key: candidate.pair_key, action: 'not_same', reason: reason || 'Different people' })}
                  disabled={decide.isPending}
                  data-testid="duplicate-reject-btn"
                >
                  <UserX className="mr-1 h-4 w-4" /> Different people
                </Button>
                <Button variant="ghost"
                        onClick={() => setSkipped((s) => [...s, candidate.pair_key])}
                        data-testid="duplicate-skip-btn">Skip for now</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {(history?.decisions || []).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Decisions already made</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pair</TableHead>
                  <TableHead>Answer</TableHead>
                  <TableHead>Who</TableHead>
                  <TableHead>Why</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.decisions.slice(0, 12).map((d) => (
                  <TableRow key={d.id} data-testid={`duplicate-history-${d.id}`}>
                    <TableCell className="text-sm">{d.left_name} · {d.right_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={d.action === 'same_person'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 bg-slate-50 text-slate-600'}>
                        {d.action === 'same_person' ? 'linked' : 'different people'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{d.decided_by}</TableCell>
                    <TableCell className="text-xs text-slate-500">{d.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== PAGE ====================

export default function AdminPeopleDirectory() {
  const [person, setPerson] = useState(null);
  const queryClient = useQueryClient();

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6" data-testid="people-directory-page">
      <div>
        <Link to="/admin" className="text-xs text-slate-500 hover:text-indigo-600" data-testid="people-back-link">
          ← Admin home
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <Users className="h-6 w-6 text-indigo-600" /> People
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          One way to identify somebody across the church, events, the college and the ministries —
          while their records stay where they belong.
        </p>
      </div>

      <Tabs defaultValue="directory">
        <TabsList>
          <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="tab-duplicates">Duplicate review</TabsTrigger>
        </TabsList>

        <TabsContent value="directory" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-6">
              <PersonPicker
                selected={person}
                onSelect={setPerson}
                onClear={() => setPerson(null)}
                label="Find someone"
              />
            </CardContent>
          </Card>
          {person && (
            <PersonProfile
              person={person}
              onPledged={() => queryClient.invalidateQueries({ queryKey: ['giving-statement'] })}
            />
          )}
        </TabsContent>

        <TabsContent value="duplicates" className="mt-4">
          <DuplicateReview />
        </TabsContent>
      </Tabs>
    </div>
  );
}
